import { useQuery } from '@apollo/client';
import { GET_ANALYTICS_QUERY } from './queries';
import { Chart } from '../../components/Chart/Chart';
import ChartLoader from '../../components/Chart/ChartLoader';
import { Analytics, DateRangeInput, PeriodType } from '@shopify-multiseller/graphql/partner/types';
import { Card, CardContent } from '../../components/primitives';

export interface DashboardChartProps {
  dateRange: DateRangeInput;
  period: PeriodType;
}

/**
 * Fulfilled orders this period against the last one. The period selector no
 * longer lives here — it scopes the whole dashboard, so it belongs beside the
 * greeting rather than on top of one card.
 */
const DashboardChart: React.FC<DashboardChartProps> = ({ dateRange, period }) => {
  // Derived from the query directly — no mirror state or loading context.
  const { data, error } = useQuery<{ analytics: Analytics }, { dateRange: DateRangeInput }>(
    GET_ANALYTICS_QUERY,
    { variables: { dateRange }, fetchPolicy: 'cache-and-network' },
  );
  const analytics = data?.analytics;

  // Inside the card, at the card's own weight: the chart is one panel on the
  // page and its failure should not outshout the page.
  if (error)
    return (
      <Card>
        <CardContent className="p-5">
          <h2 className="text-sm font-semibold text-foreground">Fulfilled orders</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            This chart didn&apos;t load. Reload the page to try again.
          </p>
        </CardContent>
      </Card>
    );

  return (
    <Card>
      <CardContent className="p-5">
        {analytics ? (
          <Chart
            title="Fulfilled orders"
            current={{
              values: analytics.currentPeriodOrders,
              quantity: analytics.currentPeriodOrdersQuantity,
              total: analytics.currentPeriodOrdersValue,
            }}
            previous={{
              values: analytics.previousPeriodOrders,
              quantity: analytics.previousPeriodOrdersQuantity,
              total: analytics.previousPeriodOrdersValue,
            }}
            period={period}
          />
        ) : (
          <ChartLoader />
        )}
      </CardContent>
    </Card>
  );
};

export default DashboardChart;
